/**
 * Pure tour selection rules, shared by the provider and `useTour`. No DOM, no React.
 *
 * The same rules run on mobile; keep the two in step (see the plan's "Client step filtering").
 */
import type {
  Tour,
  TourKind,
  TourProgress,
  TourStep,
  TourStepInteraction,
} from "../../types/tours";

/**
 * Every interaction this build has behaviour for.
 *
 * `satisfies Record<TourStepInteraction, true>` is the point of the shape: adding a value to
 * `TourStepInteraction` fails to compile here until the behaviour for it is acknowledged, which a
 * plain array of strings would not have caught. One copy only — the response guard in
 * `services/tours.ts` narrows with the same predicate rather than keeping a second list that can
 * drift from this one.
 */
const STEP_INTERACTIONS = {
  None: true,
  ClickToContinue: true,
  OpenAnchor: true,
} satisfies Record<TourStepInteraction, true>;

/**
 * Whether a value from the API is an interaction this build knows. Not redundant with the type: a
 * step can arrive from a newer API carrying a value this client has never heard of.
 */
export const isTourStepInteraction = (value: unknown): value is TourStepInteraction =>
  typeof value === "string" && Object.hasOwn(STEP_INTERACTIONS, value);

/**
 * What a step asks the coach to do, defaulted safely.
 *
 * Absent (an API older than the field) and anything this build does not recognise both read as
 * "None", which is the behaviour every step had before interactive steps existed: read it, press
 * Next.
 */
export const stepInteraction = (step: TourStep): TourStepInteraction =>
  isTourStepInteraction(step.interaction) ? step.interaction : "None";

export interface RenderContext {
  /** Anchor ids this client build knows how to find. */
  knownAnchors: ReadonlySet<string>;
  /**
   * The user's page permissions. `undefined` means unrestricted: the field is absent for an older
   * API, and admins are unrestricted (see `permissionsForTours`).
   */
  permissions?: readonly string[];
}

/** "full" is a first run; "new" shows only steps added since the user last saw the tour. */
export type TourRunMode = "full" | "new";

export interface TourRun {
  tour: Tour;
  steps: TourStep[];
  mode: TourRunMode;
  /** A help-button replay. Records nothing. */
  replay: boolean;
  /** A system admin previewing a draft. Records nothing, and chains nothing after it. */
  preview?: boolean;
  /**
   * A Guided tours replay of an entry that names no data tour (`dataTourKey: null`). Chains nothing
   * after it: Players has "Open a player" as an entry of its own, and running it straight after
   * step 14 of 14 as "Step 1 of 6" read as the same tour starting over.
   */
  standalone?: boolean;
}

const ADMIN_ROLES = new Set(["admin", "system_admin"]);

/**
 * The permission list tours should filter by. Admins and system admins always reach every page,
 * matching `hasPermission` in `utils/permissions.ts`, so their list is treated as unrestricted even
 * if a partial one were ever sent.
 */
export const permissionsForTours = (
  role: string | undefined,
  permissions: readonly string[] | undefined
): readonly string[] | undefined =>
  role && ADMIN_ROLES.has(role) ? undefined : permissions;

export const isStepRenderable = (step: TourStep, context: RenderContext): boolean => {
  if (step.anchor !== null && !context.knownAnchors.has(step.anchor)) return false;
  if (step.requiredPermission === null || context.permissions === undefined) return true;
  return context.permissions.includes(step.requiredPermission);
};

export const renderableSteps = (tour: Tour, context: RenderContext): TourStep[] =>
  tour.steps.filter((step) => isStepRenderable(step, context));

/**
 * The run a tour is due for, or null when it should not start.
 *
 * - No progress row: every renderable step.
 * - A progress row: only renderable steps not yet seen, as "new".
 * - Nothing to show either way: null, and nothing is recorded.
 */
export const dueRun = (
  tour: Tour,
  progress: TourProgress | undefined,
  context: RenderContext
): TourRun | null => {
  const steps = renderableSteps(tour, context);
  if (!progress) {
    return steps.length > 0 ? { tour, steps, mode: "full", replay: false } : null;
  }
  const seen = new Set(progress.seenStepKeys);
  const unseen = steps.filter((step) => !seen.has(step.key));
  return unseen.length > 0 ? { tour, steps: unseen, mode: "new", replay: false } : null;
};

/** Every renderable step, regardless of progress. Null when there is nothing to show. */
export const replayRun = (tour: Tour, context: RenderContext): TourRun | null => {
  const steps = renderableSteps(tour, context);
  return steps.length > 0 ? { tour, steps, mode: "full", replay: true } : null;
};

export const tourKeyFor = (featureKey: string, kind: "Page" | "Data"): string =>
  `${featureKey}.${kind === "Page" ? "page" : "data"}`;

/**
 * Names the tours a page runs, when they are not the feature's own `${feature}.page` and
 * `${feature}.data`. Both are optional and default to exactly those keys.
 */
export interface TourKeyOverrides {
  /** The tour the page leads with: a Page tour (e.g. "schedule.catalog") or a FlowStep tour. */
  pageTourKey?: string;
  /** The data tour to chain when the page has data. `null` means this page has none. */
  dataTourKey?: string | null;
}

export interface ResolvedTourKeys {
  page: string;
  data: string | null;
}

export const resolveTourKeys = (
  featureKey: string,
  { pageTourKey, dataTourKey }: TourKeyOverrides = {}
): ResolvedTourKeys => ({
  page: pageTourKey ?? tourKeyFor(featureKey, "Page"),
  data: dataTourKey === undefined ? tourKeyFor(featureKey, "Data") : dataTourKey,
});

export const findTour = (
  tours: readonly Tour[],
  featureKey: string,
  kind: TourKind
): Tour | undefined => tours.find((tour) => tour.feature === featureKey && tour.kind === kind);

export const findProgress = (
  progress: readonly TourProgress[],
  tourKey: string
): TourProgress | undefined => progress.find((entry) => entry.tourKey === tourKey);

/**
 * What to run when the page tour for a feature ends: the data tour, but only when the page has
 * data to point at. A replayed page tour chains a replayed data tour.
 */
export const chainedDataRun = (
  tours: readonly Tour[],
  progress: readonly TourProgress[],
  ended: TourRun,
  hasData: boolean,
  context: RenderContext
): TourRun | null => {
  // Only the feature's own page tour chains. A named page tour (the schedule's catalog) is a
  // different view, and its feature's data rows are not what that view shows.
  if (ended.tour.kind !== "Page" || !hasData) return null;
  if (ended.tour.key !== tourKeyFor(ended.tour.feature, "Page")) return null;
  const dataTour = findTour(tours, ended.tour.feature, "Data");
  if (!dataTour) return null;
  return ended.replay
    ? replayRun(dataTour, context)
    : dueRun(dataTour, findProgress(progress, dataTour.key), context);
};

/** Folds a finished run's steps into the progress list, as the server's union merge will. */
export const withRecordedRun = (
  progress: readonly TourProgress[],
  run: TourRun,
  outcome: TourProgress["outcome"]
): TourProgress[] => {
  const existing = findProgress(progress, run.tour.key);
  const seenStepKeys = Array.from(
    new Set([...(existing?.seenStepKeys ?? []), ...run.steps.map((step) => step.key)])
  );
  const updated: TourProgress = { tourKey: run.tour.key, outcome, seenStepKeys };
  return existing
    ? progress.map((entry) => (entry.tourKey === run.tour.key ? updated : entry))
    : [...progress, updated];
};

/** Whether a feature has any tour with at least one renderable step. */
export const featureHasTours = (
  tours: readonly Tour[],
  featureKey: string,
  context: RenderContext
): boolean =>
  tours.some(
    (tour) => tour.feature === featureKey && renderableSteps(tour, context).length > 0
  );
